import { Box, Radio, Tooltip } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { formatDate } from 'core/utils';
import { TradeAgreementUsage } from 'quotes/enums';
import { ITradeAgreement } from 'quotes/types';
import { isTradeAgreementExpired } from 'quotes/utils';

import { getTradeAgreementUsage, ITradeAgreementUsageContext } from '../../../utils';

interface IRadioSelectionCellParams
  extends ICellRendererParams<ITradeAgreement>,
    Partial<ITradeAgreementUsageContext> {}

// AG-Grid has no radio selection control, so this mirrors the row's single-row selection.
export const RadioSelectionCell = ({
  data,
  node,
  defaultRowId,
  heldRowId,
  isDefaultInUse,
}: IRadioSelectionCellParams) => {
  const [isSelected, setIsSelected] = useState(!!node?.isSelected());

  useEffect(() => {
    if (!node) {
      return;
    }

    const onRowSelected = () => setIsSelected(!!node.isSelected());

    onRowSelected();
    node.addEventListener('rowSelected', onRowSelected);

    return () => node.removeEventListener('rowSelected', onRowSelected);
  }, [node]);

  if (!data) {
    return null;
  }

  const isExpired = isTradeAgreementExpired(data);
  const isDefaultInUseRow =
    TradeAgreementUsage.DEFAULT_IN_USE ===
    getTradeAgreementUsage(data, { defaultRowId, heldRowId, isDefaultInUse });
  const disabled = isExpired || isDefaultInUseRow;

  // No shared `name`: a native radio group's arrow keys would fight the grid's cell navigation.
  const radio = (
    <Radio
      // The default in use is never a grid selection; its radio shows the line's cost.
      checked={isSelected || isDefaultInUseRow}
      disabled={disabled}
      onChange={() => !disabled && node.setSelected(true)}
      slotProps={{ input: { 'aria-label': t('labelSelectSpa', { code: data.spaCostSource }) } }}
    />
  );

  if (!disabled) {
    return radio;
  }

  // A disabled input fires no mouse events, so the tooltip listens on a wrapper.
  return (
    <Tooltip
      title={
        isExpired
          ? t('messageTradeAgreementExpired', { date: formatDate(data.spaCostEndDate) })
          : t('tooltipSpaAlreadyInUse')
      }
      placement="top"
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {radio}
      </Box>
    </Tooltip>
  );
};
